import { MemberTableSubject } from "@components/common/faro-table/faro-table-types";
import { RemoveSingleMemberFromEntity } from "@components/remove-single-member-from-entity";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import {
  isGroupMember,
  isProjectMemberBase,
} from "@custom-types/member-type-guards";
import { BaseMemberProps } from "@custom-types/member-types";
import { SdbProject } from "@custom-types/project-types";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useHasUserValidRoleGroupLevel } from "@hooks/access-control/use-has-user-valid-role-group-level";
import { useHasUserValidPermissionProjectLevel } from "@hooks/permission-control/use-has-user-valid-permission-project-level";
import { TeamMemberActions } from "@pages/members/teams/team-member-actions";
import { ActiveMemberActions } from "@pages/settings/subscription-settings/active-members/active-member-actions";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { getCompanyByIdSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import { useMemo } from "react";

interface Props extends BaseCompanyIdProps, BaseMemberProps {
  /** The selected project if there is one */
  selectedProject?: SdbProject | null;

  /** Recognizes on which subject this component is being used */
  subjectType: MemberTableSubject;

  /** The selected group if there is one */
  group?: FaroCoreApiTypes.GroupDetailResponse | null;

  /** The selected team if there is one */
  team?: FaroCoreApiTypes.Team;
}

/** Content to show for options member column */
export function OptionsMemberColumn({
  companyId,
  selectedProject,
  subjectType,
  member,
  group,
  team,
}: Props): JSX.Element | null {
  const selectedSdbCompany = useAppSelector(getCompanyByIdSelector(companyId));

  const { canDeleteMemberFromProject } =
    useHasUserValidPermissionProjectLevel();

  const { canDeleteMemberFromGroup } = useHasUserValidRoleGroupLevel({
    selectedGroup: group ?? null,
  });

  const { canDeleteMemberFromCompany } = useHasUserValidRoleCompanyLevel();

  const shouldHideTheProjectOptions = useMemo(
    () => selectedProject && selectedProject.archivingState === "ARCHIVED",
    [selectedProject]
  );

  // If there is a selected project then show dialog to remove member from project.
  if (
    selectedProject &&
    subjectType === "project" &&
    isProjectMemberBase(member)
  ) {
    const isResourceOwner = member.role === "RESOURCE_OWNER";

    if (
      shouldHideTheProjectOptions ||
      !canDeleteMemberFromProject ||
      isResourceOwner
    ) {
      return null;
    }

    return (
      <RemoveSingleMemberFromEntity
        companyId={companyId}
        member={member}
        entity={selectedProject}
        entityName="project"
      />
    );
  }

  // If there is a selected group then show dialog to remove member from group.
  if (
    group &&
    subjectType === "group" &&
    isGroupMember(member) &&
    canDeleteMemberFromGroup
  ) {
    return (
      <RemoveSingleMemberFromEntity
        companyId={companyId}
        member={member}
        entity={group}
        entityName="group"
      />
    );
  }

  // If it is not a project member then show dialog to remove member from workspace.
  if (
    subjectType === "workspace" &&
    selectedSdbCompany &&
    !isProjectMemberBase(member) &&
    canDeleteMemberFromCompany
  ) {
    return (
      <RemoveSingleMemberFromEntity
        companyId={companyId}
        member={member}
        entity={selectedSdbCompany}
        entityName="workspace"
      />
    );
  }

  // If the options column is using for active members table.
  if (subjectType === "activeMembers" && selectedSdbCompany) {
    return <ActiveMemberActions companyId={companyId} member={member} />;
  }

  // If the options column is using for team members table.
  if (team && subjectType === "team" && selectedSdbCompany) {
    return (
      <TeamMemberActions companyId={companyId} member={member} team={team} />
    );
  }

  // If didn't match the criteria then return empty string.
  return null;
}
